import { c as _c } from "react/compiler-runtime";
import { Box, type BoxProps, HStack, Text, Textarea, type TextareaProps } from '@chakra-ui/react';
import React, { type ReactNode } from 'react';

/**
 * The "draft paper" in the handoff: a warm sheet that reads as different from the thread above it,
 * because it is the one place a person's reply sits. It uses the orange palette's own surface
 * ladder (panel, muted, border, fg), so it is cream in light mode and a dark amber in dark mode,
 * with text that clears contrast in both. No raw palette steps.
 */
export function CrmPaper(props) {
  const $ = _c(25);
  let children;
  let dim;
  let footer;
  let rest;
  let t0;
  let textareaProps;
  let tools;
  if ($[0] !== props) {
    ({
      children,
      dim,
      footer,
      minHeight: t0,
      textareaProps,
      tools,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = dim;
    $[3] = footer;
    $[4] = rest;
    $[5] = t0;
    $[6] = textareaProps;
    $[7] = tools;
  } else {
    children = $[1];
    dim = $[2];
    footer = $[3];
    rest = $[4];
    t0 = $[5];
    textareaProps = $[6];
    tools = $[7];
  }
  const minHeight = t0 === undefined ? "120px" : t0;
  let t1;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      "&:focus-within": {
        boxShadow: "0 0 0 3px {colors.colorPalette.focusRing/40}"
      }
    };
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const t2 = dim ? 0.7 : 1;
  let t3;
  if ($[9] !== children || $[10] !== minHeight || $[11] !== textareaProps) {
    t3 = <Box pt={3.5} px={4}>{textareaProps ? <Textarea _focusVisible={{
        boxShadow: "none",
        outline: "none"
      }} _placeholder={{
        color: "fg.muted"
      }} bg="transparent" borderWidth={0} color="fg" fontSize="md" lineHeight="1.6" minH={minHeight} p={0} resize="none" {...textareaProps} /> : <Box fontSize="md" lineHeight="1.6" minH={minHeight} whiteSpace="pre-wrap">{children}</Box>}</Box>;
    $[9] = children;
    $[10] = minHeight;
    $[11] = textareaProps;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== tools) {
    t4 = <HStack color="colorPalette.fg" gap={1}>{tools}</HStack>;
    $[13] = tools;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== footer) {
    t5 = <HStack gap={2} wrap="wrap">{footer}</HStack>;
    $[15] = footer;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <HStack bg="colorPalette.muted" borderColor="colorPalette.border" borderTopWidth="1px" gap={2} justify="space-between" px={4} py={2.5} wrap="wrap">{t4}{t5}</HStack>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== rest || $[21] !== t2 || $[22] !== t3 || $[23] !== t6) {
    t7 = <Box bg="colorPalette.panel" borderColor="colorPalette.border" borderRadius="xl" borderWidth="1px" boxShadow="0 0 0 3px {colors.colorPalette.focusRing/20}" color="fg" colorPalette="orange" css={t1} opacity={t2} overflow="hidden" {...rest}>{t3}{t6}</Box>;
    $[20] = rest;
    $[21] = t2;
    $[22] = t3;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}

/** The small note above the paper: "Drafted by AI" on the left, what is locked on the right. */
export function CrmPaperLabel(props) {
  const $ = _c(8);
  let t0;
  if ($[0] !== props.children) {
    t0 = <Text color="fg.subtle" textStyle="labelXs">{props.children}</Text>;
    $[0] = props.children;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.aside || $[3] !== props.asideTone) {
    t1 = props.aside ? <Text color="colorPalette.fg" colorPalette={props.asideTone === "sent" ? "green" : "yellow"} fontSize="xs" fontWeight="bold">{props.aside}</Text> : null;
    $[2] = props.aside;
    $[3] = props.asideTone;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = <HStack justify="space-between" mb={2}>{t0}{t1}</HStack>;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}